import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Calendar } from 'primereact/calendar';
import { CustomButton } from '../../../components/CustomButton';
import Loader from '../../../components/Loader';
import { useLayout } from '../../../context/LayoutContext';
import { getPricingAnalytics } from '../../../reducers/wca/insights/insight-action';
import { formatDateForAPI } from '../../../utils/commonUtils';
import DateRangePicker from '../../../components/common/DateRangePicker';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import styles from "./stylesheets/Insight.module.css"

const Insight = ({ metadataId }) => {
  const dispatch = useDispatch();
  const { toast } = useLayout();

  const { usageData, usageLoading, usageError, usageLoaded } = useSelector(
    (state) => state.insight,
  );

  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const [toDate, setToDate] = useState(new Date());

  // =============================
  // 🔹 Load Data
  // =============================
  const loadData = async () => {
    try {
      await dispatch(
        getPricingAnalytics({
          metadataId,
          fromDate: formatDateForAPI(fromDate),
          toDate: formatDateForAPI(toDate),
        }),
      ).unwrap();
    } catch (err) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load analytics',
      });
    }
  };

  useEffect(() => {
    if (metadataId && !usageLoaded) {
      loadData();
    }
  }, [metadataId]);

  // =============================
  // 🔹 Summary Calculation
  // =============================
  const usageSummary = useMemo(() => {
    const summary = {};

    usageData.forEach((item) => {
      const key = item.pricing_category || 'UNKNOWN';

      if (!summary[key]) {
        summary[key] = { volume: 0, cost: 0 };
      }

      summary[key].volume += item.volume || 0;
      summary[key].cost += item.cost || 0;
    });

    return summary;
  }, [usageData]);

  const total = useMemo(() => {
    return Object.values(usageSummary).reduce(
      (acc, val) => {
        acc.volume += val.volume;
        acc.cost += val.cost;
        return acc;
      },
      { volume: 0, cost: 0 },
    );
  }, [usageSummary]);

  const formatDate = (ts) => {
    const d = new Date(ts * 1000);
    return d.toLocaleDateString('en-GB');
  };

   const dateBody = (row) => {
    const d = new Date(row.start * 1000);
    return d.toLocaleDateString('en-GB');
  };

  const costBody = (row) => `₹${row.cost?.toFixed(4)}`;

  // =============================
  // 🔹 UI
  // =============================
  return (
    <div className="p-3">
      <Loader visible={usageLoading} />

      <h2 className="mb-3">Usage & Pricing Analytics</h2>

      {/* 🔹 FILTER CARD */}
      <div className="shadow-2 border-round p-3 mb-3 bg-white">
        <div className="flex gap-3 align-items-end flex-wrap">
          
          <DateRangePicker
          fromDate={fromDate}
          toDate={toDate}
          handleFromDate={setFromDate}
          handleToDate={setToDate}
        />

          <CustomButton
            varient="filled"
            label={usageLoading ? 'Loading...' : 'Search'}
            onClick={loadData}
            disabled={usageLoading}
          />
        </div>
      </div>

      {/* 🔹 ERROR */}
      {usageError && (
        <div className="p-3 border-round bg-red-50 text-red-500 mb-3">Failed to load analytics</div>
      )}

      {/* 🔹 EMPTY */}
      {!usageLoading && usageLoaded && usageData.length === 0 && (
        <div className="text-center p-4 text-500">📊 No usage data found for selected range</div>
      )}

      {/* 🔹 TABLE */}
      {usageData.length > 0 && (
        <div className="shadow-2 border-round overflow-hidden mb-4">
         <DataTable
            value={usageData}
              key="id"
            rows={10}
            responsiveLayout="scroll"
            className="skalebot-table"
          >
            <Column header="Date" body={dateBody} />
            <Column field="pricing_category" header="Category" />
            <Column field="volume" header="Volume" />
            <Column header="Cost (₹)" body={costBody} />
          </DataTable>
        </div>
      )}

      {/* 🔹 SUMMARY */}
      {Object.keys(usageSummary).length > 0 && (
  <div className={styles.usageSummarySection}>
    <h3 className={styles.usageSummaryTitle}>Summary</h3>

    <div className={styles.usageSummaryCards}>
      {Object.entries(usageSummary).map(([category, totals]) => (
        <div
          key={category}
          className={styles.usageSummaryCard}
          style={{ '--cat-color': '#6366f1' }} // you can map colors later
        >
          <div className={styles.usageSummaryCardHeader}>
            <span
              className={styles.usageSummaryDot}
              style={{ background: '#6366f1' }}
            />
            <span className={styles.usageSummaryCategory}>{category}</span>
          </div>

          <div className={styles.usageSummaryStats}>
            <div className={styles.usageSummaryStat}>
              <span className={styles.usageSummaryStatLabel}>Total Volume</span>
              <span className={styles.usageSummaryStatValue}>
                {totals.volume.toLocaleString()}
              </span>
            </div>

            <div className={styles.usageSummaryDivider} />

            <div className={styles.usageSummaryStat}>
              <span className={styles.usageSummaryStatLabel}>Total Cost</span>
              <span className={`${styles.usageSummaryStatValue} ${styles.cost}`}>
                ₹{totals.cost.toFixed(4)}
              </span>
            </div>
          </div>
        </div>
      ))}

      {/* 🔹 TOTAL */}
      <div className={`${styles.usageSummaryCard} ${styles.usageSummaryCardTotal}`}>
        <div className={styles.usageSummaryCardHeader}>
          <span
            className={styles.usageSummaryDot}
            style={{ background: '#333' }}
          />
          <span className={styles.usageSummaryCategory}>Grand Total</span>
        </div>

        <div className={styles.usageSummaryStats}>
          <div className={styles.usageSummaryStat}>
            <span className={styles.usageSummaryStatLabel}>Total Volume</span>
            <span className={styles.usageSummaryStatValue}>
              {Object.values(usageSummary)
                .reduce((s, v) => s + v.volume, 0)
                .toLocaleString()}
            </span>
          </div>

          <div className={styles.usageSummaryDivider} />

          <div className={styles.usageSummaryStat}>
            <span className={styles.usageSummaryStatLabel}>Total Cost</span>
            <span className={`${styles.usageSummaryStatValue} ${styles.cost}`}>
              ₹
              {Object.values(usageSummary)
                .reduce((s, v) => s + v.cost, 0)
                .toFixed(4)}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default Insight;
